import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiClient } from "../../apiclient/apiclient";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, parse } from "date-fns";
// dd-MM-yyyy -> Date
const parseDDMMYYYY = (value) => {
  if (!value) return null;
  return parse(value, "dd-MM-yyyy", new Date());
};

// Date -> dd-MM-yyyy
const formatDDMMYYYY = (date) => {
  if (!date) return "";
  return format(date, "dd-MM-yyyy");
};


const EditProfile = () => {
  const { id } = useParams();          // if id exists → EDIT
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    password: "",
    image: "",
    gender: "",
    address: "",
    dateofbirth: "",
    role: "",
  });

  const [imagePreview, setImagePreview] = useState("");
  const fileInputRef = useRef(null);

  // We'll use a native date input for actual value (ISO yyyy-mm-dd)
  // and display dd-mm-yyyy visually via `DateInputOverlay`.

  /* ================= PREFILL DATA (EDIT MODE) ================= */
  useEffect(() => {
    if (!isEditMode) return;

    const fetchUser = async () => {
      try {
        const res = await apiClient.get(`/user/${id}`);
        const u = res.data.user;

        const nameArr = (u.name || "").split(" ");
        const firstName = nameArr[0] || "";
        const lastName = nameArr.slice(1).join(" ") || "";

        setForm({
          firstName,
          lastName,
          phone: u.phone || "",
          email: u.email || "",
          password: "",               // ❌ never prefill password
          gender: u.gender || "",
          address: u.address || "",
          dateofbirth: u.dateofbirth ? u.dateofbirth.split("T")[0] : "",
          role: u.role || "",
          image: "",
        });

        if (u.image) {
          const base = (
            process.env.REACT_APP_IMAGE_URL || process.env.REACT_APP_BACKEND_URL || "http://localhost:5000"
          )
          
            .replace(/\/uploads\/?$/i, '')
            .replace(/\/$/, "");

          setImagePreview(`${base}${u.image.startsWith("/") ? u.image : "/" + u.image}`);
        }
      } catch (err) {
        toast.error("Failed to load profile");
      }
    };

    fetchUser();
  }, [id, isEditMode]);

  /* ================= HANDLERS ================= */
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setForm(prev => ({ ...prev, image: file }));
    setImagePreview(URL.createObjectURL(file));
  };

  const handleCancel = () => {
    navigate(-1);
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.firstName || !form.lastName || !form.email || !form.role) {
      toast.error("Please fill all required fields");
      return;
    }

    if (!isEditMode && !form.password) {
      toast.error("Password is required for new user");
      return;
    }

    try {
      const formData = new FormData();
      
      // Build form data - skip firstName/lastName and combine them into 'name' for updates
      Object.entries(form).forEach(([key, value]) => {
        if (!value) return;
        
        // Skip firstName and lastName - we'll handle them separately
        if (key === "firstName" || key === "lastName") return;
        
        formData.append(key, value);
      });
      
      if (isEditMode) {
        // ✏️ UPDATE - backend expects 'name' field (combined firstName + lastName)
        formData.append("name", `${form.firstName} ${form.lastName}`);
        
        await apiClient.put(`/user/${id}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Profile updated successfully");
      } else {
        // ➕ ADD - backend expects firstName and lastName separately
        formData.append("firstName", form.firstName);
        formData.append("lastName", form.lastName);
        
        await apiClient.post("/superadmin/create-user", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("User created successfully");
      }

      navigate(-1);
    } catch (error) {
      toast.error(error.response?.data?.message || "Operation failed");
    }
  };

  return (
    <div>
      {/* Main Heading Section cloned from settings page */}
      <div className="w-full border-b pt-3 ps-4 pb-4 border-slate-200 bg-blue-50">
        <h1 className="text-xl font-bold text-slate-800">{isEditMode ? "Edit Profile" : "Add New User"}</h1>
        <p className="text-sm text-slate-500 mt-1">
          {isEditMode
            ? "Update the user details below."
            : "Fill in the user details below to add a new user."}
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 py-4 px-6"
      >
        {/* First Name */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-slate-700">First Name</label>
          <input
            type="text"
            name="firstName"
            placeholder="Enter first name"
            value={form.firstName}
            onChange={handleChange}
            className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full"
            autoComplete="off"
          />
        </div>

        {/* Last Name */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-slate-700">Last Name</label>
          <input
            type="text"
            name="lastName"
            placeholder="Enter last name"
            value={form.lastName}
            onChange={handleChange}
            className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full"
            autoComplete="off"
          />
        </div>

        {/* Gender */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-slate-700">Gender</label>
          <select
            name="gender"
            value={form.gender}
            onChange={handleChange}
            className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full"
          >
            <option value="">Select gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
        </div>

        {/* Role */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-slate-700">Role</label>
          <select
            name="role"
            value={form.role}
            onChange={handleChange}
            className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full"
          >
            <option value="">Select role</option>
            <option value="superAdmin">Super Admin</option>
            <option value="admin">Admin</option>
            <option value="delivery-boy">Delivery Boy</option>
            <option value="coAdmin">Co Admin</option>
          </select>
        </div>

        {/* Phone Number */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-slate-700">Phone Number</label>
          <input
            type="text"
            name="phone"
            placeholder="Enter phone number"
            value={form.phone}
            onChange={handleChange}
            className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full"
            autoComplete="off"
          />
        </div>

        {/* Email */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-slate-700">Email</label>
          <input
            type="email"
            name="email"
            placeholder="Enter email"
            value={form.email}
            onChange={handleChange}
            className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full"
            autoComplete="off"
          />
        </div>

        {/* Password (only for add mode) */}
        {!isEditMode && (
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-slate-700">Password</label>
            <input
              type="password"
              name="password"
              placeholder="Enter password"
              value={form.password}
              onChange={handleChange}
              className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full"
              autoComplete="off"
            />
          </div>
        )}

        {/* Date of Birth */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-slate-700">Date of birth</label>
          <input
            type="date"
            name="dateofbirth"
            value={form.dateofbirth}
            onChange={handleChange}
            className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full"
          />
        </div>

        {/* Address */}
        <div className="flex flex-col gap-1 md:col-span-2">
          <label className="text-xs font-medium text-slate-700">Address</label>
          <input
            type="text"
            name="address"
            placeholder="Enter address"
            value={form.address}
            onChange={handleChange}
            className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full"
            autoComplete="off"
          />
        </div>

        {/* Profile Image */}
        <div className="flex flex-col gap-1 md:col-span-2">
          <label className="text-xs font-medium text-slate-700">Profile Image</label>
          <input
            type="file"
            name="image"
            accept="image/*"
            onChange={handleImageChange}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full"
          />
          {imagePreview && (
            <img src={imagePreview} alt="Preview" className="mt-2 w-20 h-20 object-cover rounded-full border" />
          )}
        </div>

        {/* Submit/Cancel Buttons */}
        <div className="flex gap-3 md:col-span-2 mt-2">
          <button
            type="submit"
            className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 py-2 rounded-md transition-colors text-sm"
          >
            {isEditMode ? "Update User" : "Add User"}
          </button>
          <button
            type="button"
            onClick={handleCancel}
            className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold px-6 py-2 rounded-md transition-colors text-sm border border-slate-200"
          >
            Cancel
          </button>
        </div>
      </form>
      </div>
    
  );
};

export default EditProfile;
